import { Select, Spin } from 'antd'
import debounce from 'lodash/debounce'
import isObject from 'lodash/isObject'
import isEqual from 'lodash/isEqual'
import { useEffect, useState } from 'react'
import { usePrevious } from '../usePrevious'

/* Props explaining:

remoteSearch: {
  filterOption: false, 
      // From Antd
  mode: "multiple", 
      // From Antd
  {...rest}  
      // Can contain ANTD Select props from documentation
  paramSearchQuery: 'search', 
      **REQUIRED** The actual query for URL 
  apiFn(params), 
      **REQUIRED** Should return an array of desired values. Can use fetch, axios etc ...
}

apiFn example : 
.then(response => response.json())
.then(body => {
  // OBJECT with text - value
  result = body.data.map(user => ({
    text: `${user.first_name} ${user.last_name}`,
    value: user.id,
  }));

  // ARRAY as desired form
  result = body.data.map(user => user.first_name);
});

*/

let latestFetch = 0

const RemoteSelect = props => {
  const {
    onChange,
    itemConfig: {
      options,
      remoteSearch: { apiFn, paramSearchQuery, ...restRS },
      disabled,
      ...restItemConfig
    },
    // pass 'value' from Form.Item to <Select>
    ...restProps
  } = props

  const [data, setData] = useState([])
  const [fetching, setFetching] = useState(false)
  const [preOptions, setPreOptions] = useState(options || [])

  const previousOptions = usePrevious(options)

  useEffect(() => {
    if (previousOptions !== undefined && !isEqual(previousOptions, options)) {
      setPreOptions(options)
      // console.log('props.val', props.itemConfig);
    }
  }, [options, previousOptions])

  const fetchData = async value => {
    // console.log('fetchData', value);
    // No need to call API search without value
    if (!value) {
      return
    }

    setData([])
    setFetching(true)

    const params = {}
    params[paramSearchQuery] = value

    // Update state 'data' with latest triggered fetch
    latestFetch += 1
    const fetchID = latestFetch

    const data = await apiFn(params)

    if (fetchID === latestFetch) {
      setData(data)
      setFetching(false)
      setPreOptions([])
    }
  }

  const handleChange = (value, option) => {
    onChange(value, option)
  }

  const fetchDataDebounced = debounce(fetchData, 800)

  const dataFiltered = data.filter(x => !preOptions.some(y => y.value === x.value))

  const newData = dataFiltered.concat(preOptions)

  const getOptions = () => {
    return newData.map(d => {
      const isString = !isObject(d)

      return {
        value: isString ? d : d.value,
        label: isString ? d : d.text,
        extra: d,
      }
    })
  }

  return (
    <Select
      {...restRS}
      // DO NOT modify below default props
      {...restProps}
      disabled={disabled}
      showSearch
      allowClear
      onSearch={fetchDataDebounced}
      onFocus={() => fetchDataDebounced(' ')}
      onChange={handleChange}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      options={getOptions()}
      {...restItemConfig}
    />
  )
}

export default RemoteSelect
